import React, { useEffect, useState, useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Button from '../../components/Button/Button'
import { getPaymentLinkByToken, payByToken } from '../../features/paymentLink/paymentLink'
import { getLinkToken } from '../../utils/linkToken'

const SCRIPT_LOAD_TIMEOUT_MS = 20000

const getCollectJsUrl = () => process.env.REACT_APP_NMI_COLLECT_JS_URL || 'https://secure.nmi.com/token/Collect.js'
const getTokenizationKey = () => process.env.REACT_APP_NMI_TOKENIZATION_KEY || ''

const readThemeToken = (name, fallback) => {
  if (typeof window === 'undefined' || !window.getComputedStyle) return fallback
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name)
  return (value || '').trim() || fallback
}

const buildFieldCss = () => ({
  customCss: {
    'font-family': "Inter, system-ui, sans-serif",
    'font-size': '16px',
    'background-color': 'transparent',
    color: readThemeToken('--color-text-primary', '#101828'),
  },
  focusCss: {
    color: readThemeToken('--color-text-primary', '#101828'),
  },
  placeholderCss: {
    color: readThemeToken('--color-text-secondary', '#667085'),
  },
  invalidCss: {
    color: readThemeToken('--color-error-main', '#D92D20'),
  },
  validCss: {
    color: readThemeToken('--color-text-primary', '#101828'),
  },
})

const FIELD_LABELS = {
  ccnumber: 'card number',
  ccexp: 'expiry date',
  cvv: 'security code',
}

const describeValidation = (field, message) => {
  const label = FIELD_LABELS[field]
  if (!label) return message || 'Please check your card details'
  if (!message || /empty|required/i.test(message)) return `Please enter the ${label}`
  return `Please check the ${label}`
}

const loadCollectJs = () => new Promise((resolve, reject) => {
  if (window.CollectJS) return resolve(window.CollectJS)
  const script = document.createElement('script')
  const timer = setTimeout(() => reject(new Error('Failed to load payment library')), SCRIPT_LOAD_TIMEOUT_MS)
  script.src = getCollectJsUrl()
  script.async = true
  script.setAttribute('data-tokenization-key', getTokenizationKey())
  script.onload = () => {
    clearTimeout(timer)
    resolve(window.CollectJS)
  }
  script.onerror = () => {
    clearTimeout(timer)
    reject(new Error('Failed to load payment library'))
  }
  document.body.appendChild(script)
})

const PageShell = ({ children }) => (
  <div className="min-h-screen w-full bg-background flex items-start sm:items-center justify-center p-4 sm:p-10">
    <div className="w-full max-w-[480px] flex flex-col items-start gap-6 pt-6 pb-10 px-6 rounded-2xl border border-divider bg-paper">
      {children}
    </div>
  </div>
)

const StateMessage = ({ children }) => (
  <PageShell>
    <p className="w-full text-center text-text-primary font-sans text-lg leading-[150%]">{children}</p>
  </PageShell>
)

const FieldRow = ({ label, id }) => (
  <div className="w-full flex flex-col items-start gap-1.5">
    <span className="text-text-secondary font-sans text-[12px] font-bold leading-[150%] uppercase">{label}</span>
    <div className="w-full h-[56px] px-3 flex items-center rounded-lg border border-divider bg-background">
      <div id={id} className="w-full" />
    </div>
  </div>
)

const PayLink = () => {
  // The token comes from the fragment, which never reaches a server. `useParams` is the
  // fallback for links emailed in the legacy /pay/:token shape before this shipped.
  const { token: pathToken } = useParams()
  const token = getLinkToken() || pathToken
  const dispatch = useDispatch()

  const [link, setLink] = useState(null)
  const [status, setStatus] = useState('loading')
  const [error, setError] = useState(null)
  const [paying, setPaying] = useState(false)
  const submittingRef = useRef(false)

  useEffect(() => {
    let active = true
    dispatch(getPaymentLinkByToken({ token }))
      .unwrap()
      .then((data) => {
        if (!active) return
        setLink(data)
        setStatus(data.status || 'pending')
      })
      .catch((err) => {
        if (!active) return
        setError(typeof err === 'string' ? err : (err?.message || 'Invalid link'))
        setStatus('invalid')
      })
    return () => { active = false }
  }, [dispatch, token])

  const submitToken = useCallback((paymentToken) => {
    submittingRef.current = false
    setPaying(true)
    dispatch(payByToken({ token, paymentToken }))
      .unwrap()
      .then((data) => {
        setPaying(false)
        if (data.status === 'paid') setStatus('paid')
        else if (data.status === 'processing') setStatus('processing')
        else if (data.status === 'declined') setError(data.message || 'Card was declined')
        else setStatus(data.status)
      })
      .catch((err) => {
        setPaying(false)
        setError(typeof err === 'string' ? err : (err?.message || 'Payment failed'))
      })
  }, [dispatch, token])

  useEffect(() => {
    if (status !== 'pending' || !getTokenizationKey()) return
    let active = true
    loadCollectJs()
      .then((CollectJS) => {
        if (!active || !CollectJS) return
        CollectJS.configure({
          variant: 'inline',
          ...buildFieldCss(),
          fields: {
            ccnumber: { selector: '#ccnumber', placeholder: 'Card Number' },
            ccexp: { selector: '#ccexp', placeholder: 'MM / YY' },
            cvv: { selector: '#cvv', placeholder: 'CVV' },
          },
          callback: (response) => {
            if (response?.token) submitToken(response.token)
            else {
              submittingRef.current = false
              setPaying(false)
              setError('Card details are invalid')
            }
          },
          validationCallback: (field, valid, message) => {
            if (!valid && submittingRef.current) {
              submittingRef.current = false
              setPaying(false)
              setError(describeValidation(field, message))
            }
          },
          timeoutDuration: 15000,
          timeoutCallback: () => {
            if (submittingRef.current) {
              submittingRef.current = false
              setPaying(false)
              setError('The payment form timed out. Please try again.')
            }
          },
        })
      })
      .catch(() => setError('Failed to load payment library'))
    return () => { active = false }
  }, [status, submitToken])

  const startPayment = () => {
    setError(null)
    if (window.CollectJS) {
      submittingRef.current = true
      setPaying(true)
      window.CollectJS.startPaymentRequest()
    } else {
      setError('Payment library not ready')
    }
  }

  if (status === 'loading') return <StateMessage>Loading…</StateMessage>
  if (status === 'invalid') return <StateMessage>{error || 'This link is invalid.'}</StateMessage>
  if (status === 'expired') return <StateMessage>This payment link has expired.</StateMessage>
  if (status === 'cancelled') return <StateMessage>This payment link was cancelled.</StateMessage>
  if (status === 'paid') return <StateMessage>Thank you! Your payment was successful.</StateMessage>
  if (status === 'processing') return <StateMessage>Thank you — we are confirming your payment. No further action is needed.</StateMessage>
  if (!getTokenizationKey()) return <StateMessage>Online payments are temporarily unavailable. Please contact support.</StateMessage>

  const amount = Number(link?.amount || 0).toFixed(2)

  return (
    <PageShell>
      <div className="w-full flex flex-col items-start gap-1">
        <h1 className="text-text-primary font-sans text-xl font-semibold leading-[150%]">Complete Your Payment</h1>
        {link?.clientName && <p className="text-text-secondary font-sans text-sm leading-[150%]">{link.clientName}</p>}
      </div>

      <div className="w-full flex flex-col items-start gap-1 py-4 px-4 rounded-xl border border-divider bg-background">
        <span className="text-text-secondary font-sans text-[12px] font-bold leading-[150%] uppercase">Amount due</span>
        <p className="text-text-primary font-sans text-[32px] font-semibold leading-[130%]">${amount}</p>
      </div>

      {error && (
        <p className="w-full py-3 px-4 rounded-lg bg-error-light text-error-main font-sans text-sm leading-[150%]">{error}</p>
      )}

      <div className="w-full flex flex-col items-start gap-4">
        <FieldRow label="Card number" id="ccnumber" />
        <div className="w-full flex items-start gap-4">
          <div className="w-1/2"><FieldRow label="Expiry" id="ccexp" /></div>
          <div className="w-1/2"><FieldRow label="CVV" id="cvv" /></div>
        </div>
      </div>

      <Button size="lg" width="w-full" disabled={paying} onClick={startPayment}>
        {paying ? 'Processing…' : `Pay $${amount}`}
      </Button>

      <p className="w-full text-center text-text-secondary font-sans text-[12px] leading-[150%]">
        Your card details are sent directly to our payment processor and never touch our servers.
      </p>
    </PageShell>
  )
}

export default PayLink
